import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import qs from 'qs'
import history from '../../../history'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { constructFilters, constructFiltersAndOmitEmpty } from '../../../helpers/listings/filters'
import { camelizeKeys } from '../../../helpers/utils'
import { selectSelectedListingId, selectShowListingSearchModal } from '../../../store/selectors/listings'
import { setSelectedListingId, toggleListingSearchModal } from '../../../actions/listings'
import { loadSuggestedLocations, setFilter } from '../../../actions/filters'
import { SavedSearchToggle } from './SavedSearchToggle'
import { BotHeadSpinWithMessage, OverlayPanel, Text } from '../..'
import PreferencesPanel from '../../shared/PreferencesPanel'
import OverlayMask from '../../shared/Routing/OverlayMask'
import { Filters } from '../Filters'
import { HomeSearchFooter } from '../HomeSearchFooter/HomeSearchFooter'
import { ListingsMap } from './ListingsMap/ListingsMap'
import { ListingsSort } from './ListingsSort/ListingsSort'
import {
  CITY,
  COUNTY,
  LOCATION_LEVELS,
  MAX_PRICE,
  MAX_SQFT,
  MIN_MONTHLY_PAYMENT,
  MAX_MONTHLY_PAYMENT,
  MAX_YEAR_BUILT,
  MIN_BATHROOMS_COUNT,
  MIN_BEDROOMS_COUNT,
  MIN_PRICE,
  MIN_SQFT,
  MIN_YEAR_BUILT,
  NEIGHBOURHOOD,
  POSTAL_CODE,
  PROPERTY_TYPES,
  STATE,
  STATUSES,
  STREET,
  MIN_ACRES,
  MAX_ACRES,
  HAS_SELLER_CONCESSION,
  DOWN_PAYMENT,
  IS_ASSUMABLE,
  HAS_OPEN_HOUSES
} from '../../../constants/listings'
import { OVERLAY_HELPERS, PATH_HELPERS } from '../../../constants/navigation'
import useViewport from '../../../hooks/useViewport'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { selectFilter, selectFiltersCount } from '../../../store/selectors/filters'
import { selectPreviouslyViewedListingId } from '../../../store/selectors/listings'
import { Button, Flex, Box } from '@homebotapp/hb-react-component-catalog'
import { HomeSearchTrackingEventLocation } from 'src/constants/tracking'
import { setActiveNavigationTab } from '../../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../../types/navigation'
import { ListViewFilterViewSpacingWrapper } from '../ListViewFilterViewSpacingWrapper'
import { SearchIcon } from '../Search/SearchBar/SearchIcon'
import { ListView } from './ListView'
import styles from './Listings.module.scss'
import { ListingsEmptyState } from './ListingsEmptyState/ListingsEmptyState'
import { useGetListingsForArea } from 'src/hooks/listings/useGetListingsForArea'
import { useFilters } from './hook/useFilters'
import { useRecoilState } from 'recoil'
import { MobileDownloadMobileAppPromptCard } from 'src/Components/homeSearch/ListingDetails/DownloadMobileAppPromptCard/Mobile/MobileDownloadMobileAppPromptCard'
import { selectNativeAccessFeature } from '../../../store/selectors/customerProfile'
import { CustomerListingsCard } from './CustomerListingsCard/CustomerListingsCard'
import { setHideMobileBottomNav } from '../../../store/slices/navigation'
import { useAppSelector } from '../../../store/hooks'
import { GoogleAutoComplete } from '../GoogleAutoComplete/GoogleAutoComplete'
import { currentSearchLocation } from '../GoogleAutoComplete/state/currentSearchLocation'
import { homeSearchMapState } from './ListingsMap/state/homeSearchMapState'
import { BoundingBoxLevel } from '../../../api/gqlaxy/gql/generated/graphql'

const MLS_WITH_DISCLAIMER_REQUIREMENTS = ['WillametteValley', 'NorthwestMLS', 'REBNY']
const PUBLIC_SEARCH_ONLY_MLS = ['BAREIS', 'Metrolist']

export const MSG = defineMessages({
  loadMore: {
    id: 'General.loadMore',
    defaultMessage: 'Load More'
  }
})

const Listings = () => {
  const queryParams = camelizeKeys(
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    })
  )
  const dispatch = useDispatch()
  const intl = useIntl()
  const isPublic = useAppSelector(state => state.auth.isPublic)
  const previouslyViewedListingId = useSelector(selectPreviouslyViewedListingId)
  const filter = useSelector(selectFilter)
  const filtersCount = useSelector(selectFiltersCount)
  const customerId = useSelector(selectCustomerId)
  const [searchLocation, setSearchLocation] = useRecoilState(currentSearchLocation)
  const hasNativeAccess = useSelector(selectNativeAccessFeature)
  const selectedListingId = useSelector(selectSelectedListingId)
  const [mapState] = useRecoilState(homeSearchMapState)
  let { filters, page, setPage } = useFilters()

  let searchAreaFromSavedSearchReferral
  // if we're coming in from a saved search that was created with a placeId, read in the place id from the url and set the searchArea filter, which will make us fetch listings from that place_id
  if (queryParams.from === 'saved_search_place_id' && queryParams.placeId && filters) {
    searchAreaFromSavedSearchReferral = { placeIdInput: { placeId: queryParams.placeId } }
  }
  if (
    queryParams.from === 'saved_search_bounding_box' &&
    queryParams.bottomRightLat &&
    queryParams.bottomRightLong &&
    queryParams.topLeftLat &&
    queryParams.topLeftLong &&
    filters
  ) {
    searchAreaFromSavedSearchReferral = {
      boundingBox: {
        bottomRight: {
          latitude: Number(queryParams.bottomRightLat),
          longitude: Number(queryParams.bottomRightLong)
        },
        topLeft: {
          latitude: Number(queryParams.topLeftLat),
          longitude: Number(queryParams.topLeftLong)
        },
        level: BoundingBoxLevel.BoundingBox
      }
    }
  }

  const {
    data: listings,
    isFetching,
    refetchListings,
    hasMore,
    location
  } = useGetListingsForArea({
    ...filters,
    searchArea: searchAreaFromSavedSearchReferral || filters?.searchArea
  })

  listings.filter(listing => !isPublic && !PUBLIC_SEARCH_ONLY_MLS.includes(listing.originatingSystemName)) // We are already removing these in the backend in gqlactus, but really just wanted to make sure we are not showing these listings in the UI other than for Public Home Search.

  const [showFilters, setShowFilters] = useState(false)
  const [isMobileViewExpanded, setIsMobileViewExpanded] = useState(false)
  const { isMediumOrAbove } = useViewport()
  const showSearchModal = useSelector(selectShowListingSearchModal)

  //Get all MLS logos and disclaimers from the listing results to pass to footer component for special disclaimer requirements
  const specialDisclaimers = useMemo(() => {
    return listings?.reduce(
      (existing, nextListing) =>
        existing.hasOwnProperty(nextListing?.originatingSystemName)
          ? existing
          : MLS_WITH_DISCLAIMER_REQUIREMENTS.includes(nextListing?.originatingSystemName)
          ? Object.assign(existing, {
              [nextListing?.originatingSystemName]: {
                mlsLogo: nextListing?.mlsLogo,
                // @ts-ignore
                disclaimer: nextListing?.disclaimer
              }
            })
          : existing,
      {}
    )
  }, [listings])

  const scrollBehavior = useMemo(() => {
    return isMediumOrAbove ? 'smooth' : 'auto'
  }, [isMediumOrAbove])

  // splitting apart this array for useEffect dependencies array
  const [propertyType0, propertyType1, propertyType2, propertyType3] = queryParams?.[PROPERTY_TYPES] || [
    undefined,
    undefined,
    undefined,
    undefined
  ]

  // splitting apart this array for useEffect dependencies array
  const [listingStatus0, listingStatus1] = queryParams?.[STATUSES] || [undefined, undefined]

  // restore scroll afer user returns from visiting listing details
  useEffect(() => {
    if (previouslyViewedListingId && listings?.length > 0) {
      // hack to allow refs on "statefulListings" to be set. Would love a better way to trigger effect when that happens
      setTimeout(() => {
        const previouslyClickedListing = document.getElementById(previouslyViewedListingId)

        if (previouslyClickedListing) {
          previouslyClickedListing?.scrollIntoView({
            behavior: scrollBehavior,
            block: 'center'
          })
        }
      }, 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previouslyViewedListingId, listings])

  useEffect(() => {
    const searchFilters = constructFilters(queryParams)
    const searchFiltersWithMonthlyExpenses = {
      ...searchFilters,
      monthlyExpensesInput: {
        minMonthlyExpenses: searchFilters.minMonthlyPayment,
        maxMonthlyExpenses: searchFilters.maxMonthlyPayment,
        downpaymentAmountDollars: searchFilters.downPayment
      },
      order: filter?.order || 'DAYS_ON_MARKET, asc'
    }
    dispatch(setFilter(searchFiltersWithMonthlyExpenses))

    // if we have bounding box query params, we are coming from saved search and we don't need to fetch suggested locations
    if (
      queryParams.topLeftLat &&
      queryParams.topLeftLong &&
      queryParams.bottomRightLat &&
      queryParams.bottomRightLong
    ) {
      return
    }

    LOCATION_LEVELS.find(currentLocationLevel => {
      let value

      if (currentLocationLevel === CITY) {
        value = queryParams[currentLocationLevel]?.split(',')[0]
      } else {
        value = queryParams[currentLocationLevel]?.split(' - ')[0]
      }
      if (value && value !== searchLocation?.name) {
        dispatch(
          loadSuggestedLocations({
            query: value,
            fullLocationName: queryParams[currentLocationLevel],
            filter: searchFiltersWithMonthlyExpenses
          })
        )
      } else if (value && !searchLocation && value !== filter?.displayName) {
        dispatch(
          loadSuggestedLocations({
            query: value,
            fullLocationName: queryParams[currentLocationLevel],
            filter: searchFiltersWithMonthlyExpenses
          })
        )
      }
    })
  }, [
    dispatch,
    location,
    queryParams?.[POSTAL_CODE],
    queryParams?.[STREET],
    queryParams?.[NEIGHBOURHOOD],
    queryParams?.[COUNTY],
    queryParams?.[CITY],
    queryParams?.[STATE],
    queryParams?.[MIN_PRICE],
    queryParams?.[MAX_PRICE],
    queryParams?.[MIN_BATHROOMS_COUNT],
    queryParams?.[MIN_BEDROOMS_COUNT],
    queryParams?.[MIN_ACRES],
    queryParams?.[MAX_ACRES],
    queryParams?.[MIN_SQFT],
    queryParams?.[MAX_SQFT],
    queryParams?.[MIN_YEAR_BUILT],
    queryParams?.[MAX_YEAR_BUILT],
    queryParams?.[HAS_SELLER_CONCESSION],
    queryParams?.[IS_ASSUMABLE],
    queryParams?.[HAS_OPEN_HOUSES],
    propertyType0,
    propertyType1,
    propertyType2,
    propertyType3,
    listingStatus0,
    listingStatus1,
    queryParams?.[DOWN_PAYMENT],
    queryParams?.[MIN_MONTHLY_PAYMENT],
    queryParams?.[MAX_MONTHLY_PAYMENT]
  ])

  // This is used so that if the filters change, it fetches different listing data.
  // GraphQL may be smart enough to do that without this method?
  useEffect(() => {
    if (!isEmpty(filters?.searchArea?.boundingBox)) {
      refetchListings()
    }
  }, [filters?.searchArea?.boundingBox])

  useEffect(() => {
    // Set the active tab to home search when we are on the
    // home search index page.
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.SEARCH))
  }, [dispatch, customerId])

  const onMarkerClick = useCallback(
    listing => {
      setIsMobileViewExpanded(true)

      if (listing?.id && selectedListingId !== listing?.id) {
        dispatch(setSelectedListingId(listing?.id))
      } else {
        dispatch(setSelectedListingId(''))
      }

      const clickedListing = document.getElementById(listing.id)

      if (clickedListing) {
        setTimeout(() => {
          clickedListing?.scrollIntoView({
            behavior: scrollBehavior,
            block: 'center'
          })
        }, 500)
      }
    },
    [customerId, dispatch, listings, scrollBehavior, selectedListingId]
  )

  const getListingsListView = () => {
    if (isFetching) {
      return (
        <BotHeadSpinWithMessage className={styles.loadingContainer}>
          <FormattedMessage id='BotHeadSpinWithMessage.ListingsListView' defaultMessage='Loading homes for you...' />
        </BotHeadSpinWithMessage>
      )
    }

    if (!listings || listings?.length === 0) {
      return <ListingsEmptyState />
    }

    return (
      <>
        <CustomerListingsCard />
        <ListView listings={listings} tagPreference={filters?.filters?.isAssumable ? 'assumableRate' : 'priceChange'} />
        {hasMore && (
          <Flex flexDirection='row' alignItems='center' justifyContent='center' marginTop='6'>
            <Button
              colorScheme='primary'
              isLoading={isFetching}
              isDisabled={isFetching}
              tracking={{
                guid: 'vwg5wZaik1L35Ftu5HcoAE',
                ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_LISTINGS_INDEX_PAGE_LIST_VIEW,
                descriptor: MSG.loadMore.defaultMessage
              }}
              onClick={() => setPage(page + 1)}
            >
              {intl.formatMessage(MSG.loadMore)}
            </Button>
          </Flex>
        )}
      </>
    )
  }

  const getListingsTotalDisplay = useCallback(() => {
    if (listings?.length === 1) {
      return '1 home'
    }
    return hasMore ? `${listings.length}+ homes` : `${listings.length} homes`
  }, [listings])

  const onLocationChange = place => {
    setSearchLocation(place)

    dispatch(toggleListingSearchModal(false))
    dispatch(setFilter({ location: undefined }))

    const listingsUrl = PATH_HELPERS.listings.buildPath(
      customerId,
      'place_id',
      place.place_id,
      constructFiltersAndOmitEmpty(filter)
    )

    const publicListingsUrl = PATH_HELPERS.publicListings.buildPath(
      'place_id',
      place.place_id,
      constructFiltersAndOmitEmpty(filter)
    )

    history.push(isPublic ? publicListingsUrl : listingsUrl)
  }

  // if we're coming from a saved search's "view all listings" button, we only know the place id or the bounding box
  // it's easier to get a display name from the 1st listing versus trying to call google apis to get the city name from the place id/bounding box
  const getSearchBarDisplayText = () => {
    if (
      queryParams.from === 'saved_search_place_id' &&
      listings &&
      listings[0] &&
      listings[0]?.address?.city &&
      listings[0]?.address?.stateOrProvince
    ) {
      return `${listings[0]?.address?.city}, ${listings[0]?.address?.stateOrProvince}`
    } else if (
      queryParams.from === 'saved_search_bounding_box' &&
      listings &&
      listings[0] &&
      listings[0]?.address?.city &&
      listings[0]?.address?.stateOrProvince
    ) {
      return `Custom area near ${listings[0]?.address?.city}, ${listings[0]?.address?.stateOrProvince}`
    } else if (searchLocation?.description) {
      return searchLocation?.description
    } else if (filter?.displayName) {
      return filter?.displayName
    } else {
      return ''
    }
  }

  return (
    <>
      {showSearchModal && (
        <GoogleAutoComplete
          onClose={() => (mapState?.center ? dispatch(toggleListingSearchModal(false)) : undefined)}
          onPlaceSelected={onLocationChange}
          types={[
            'locality',
            'postal_code',
            'administrative_area_level_1'
            // 'neighborhood', // uncomment this line to enable neighborhood search when we have the BE support
            // 'administrative_area_level_2' // uncomment this line to enable county search when we have the BE support
          ]}
        />
      )}
      <OverlayPanel
        back={OVERLAY_HELPERS.preferences.getOrigin()}
        path={OVERLAY_HELPERS.preferences.buildPath()}
        title={OVERLAY_HELPERS.getTitle('preferences', intl.formatMessage)}
        disableAnchor
        component={PreferencesPanel}
      />
      {/* @ts-ignore */}
      <OverlayMask className={styles.hideOverlayScrollbar} isExact>
        <div className={styles.listingsWrapper}>
          <ListViewFilterViewSpacingWrapper
            isMobileViewExpanded={isMobileViewExpanded}
            setIsMobileViewExpanded={setIsMobileViewExpanded}
          >
            {showFilters ? (
              <Filters onClose={() => setShowFilters(false)} />
            ) : (
              <div className={styles.listingsResults}>
                <Box
                  py={3}
                  px={2}
                  zIndex={2}
                  position='sticky'
                  top={[-1, null, 0]}
                  bg='card-bg-layer-1'
                  boxShadow='0px 0px 1px rgba(0, 0, 0, 0.1), 0px 10px 15px -3px rgba(0, 0, 0, 0.3), 0px 4px 6px -2px rgba(0, 0, 0, 0.55)'
                  _light={{
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div className={styles.searchWrapper}>
                    <Button
                      onClick={() => dispatch(toggleListingSearchModal(true))}
                      className={styles.searchBarBtn}
                      tracking={{
                        guid: '2RHzSacwujR2VbgwsNnLHQ',
                        ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_LISTINGS_INDEX_PAGE_LIST_VIEW,
                        descriptor: 'Search Bar'
                      }}
                    >
                      <div className={styles.placeholder}>
                        <SearchIcon className={styles.svg} />
                        <Text className={styles.cta} variant='neutralCool' variantCount='900' element='span'>
                          {/* use first listing address if we're coming from a saved search, because we will only have the place id in the url not the full currentSearchLocation */}
                          {getSearchBarDisplayText()}
                        </Text>
                      </div>
                    </Button>
                    <SavedSearchToggle />
                  </div>
                  <div className={styles.countWrapper}>
                    <Text element='h2' weight='bold' align='left' variant='neutralCool' variantCount='900'>
                      {!isFetching && getListingsTotalDisplay()}
                    </Text>
                    <Button
                      size='sm'
                      variant='outline'
                      data-qa='listingsResultsFilterButton'
                      aria-label='filter listings'
                      onClick={() => {
                        setShowFilters(true)
                        setIsMobileViewExpanded(true)
                        dispatch(setHideMobileBottomNav(true))
                      }}
                      tracking={{
                        guid: 'd11t3GdSz3wxkr5h5jsFhJ',
                        ui_context: HomeSearchTrackingEventLocation.HOME_SEARCH_LISTINGS_INDEX_PAGE_LIST_VIEW,
                        descriptor: 'Filters'
                      }}
                    >
                      <FormattedMessage id='HomeSearch.listings.filters.button' defaultMessage='Filters' />
                      <div className={styles.filtersCount}>{filtersCount}</div>
                    </Button>
                    <ListingsSort />
                  </div>
                </Box>
                <div
                  className={classNames({
                    [styles.loadingWrapper]: isFetching
                  })}
                >
                  {getListingsListView()}
                </div>
                <div>
                  <HomeSearchFooter specialDisclaimers={specialDisclaimers} isListingsPage={true} />
                  {hasNativeAccess && (
                    <Box margin={8}>
                      <MobileDownloadMobileAppPromptCard isLandingPage={true} />
                    </Box>
                  )}
                </div>
              </div>
            )}
          </ListViewFilterViewSpacingWrapper>
          <div className={styles.map}>
            <ListingsMap
              location={location}
              filter={filter}
              onMarkerClick={onMarkerClick}
              isDelayLoading={isFetching && !listings}
              setIsMobileViewExpanded={setIsMobileViewExpanded}
              scrollBehavior={scrollBehavior}
            />
          </div>
        </div>
      </OverlayMask>
    </>
  )
}

export default Listings
